import React, { useState } from 'react';
import { ReactComponent as UpgradePlanSvg } from '@assets/icons/diamond-upgrade.svg';
import { Dropdown, Menu } from 'antd';
import { useDispatch } from 'react-redux';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import DropdownMenuButton from '@components/EntitiesDropdownMenu/DropdownMenuButton';
import UpgradePlanTooltip from '@components/Tooltip/UpgradePlanTooltip';
import { useOrganization } from '@components/OrganizationBoundary';
import './EntitiesDropdownMenu.less';
import {
  toggleWorkspaceCreationModal,
  toggleWorkspaceInvitationModal,
  toggleWorkspaceMembersModal,
  toggleWorkspaceOrganizationDeletionModal,
  toggleWorkspaceOrganizationLeaveModal,
  toggleArchiveWorkspaceModal
} from '@redux/actions/modalAction';
import { WorkspaceItemDto } from '@api/Api';

type WorkspaceMenuProps = {
  workspace: WorkspaceItemDto;
  setIsDropDownOpen?: (v: boolean) => void;
};

export function WorkspaceMenu({
  workspace,
  setIsDropDownOpen
}: WorkspaceMenuProps) {
  const dispatch = useDispatch();
  const { currentOrganization } = useOrganization();
  const isUsersCountLimitReached =
    currentOrganization.seatsCount.count >=
    (currentOrganization.entity?.limitsV2.users ?? Number.POSITIVE_INFINITY);

  const items: ItemType[] = [
    {
      key: 'invite_to_workspace',
      label: (
        <UpgradePlanTooltip
          textBefore={
            <>
              You have reached the user limit in your account. <br />
            </>
          }
          text="to invite more people."
          isCanVisible={isUsersCountLimitReached}
          placement="right"
        >
          <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            Invite to workspace {isUsersCountLimitReached && <UpgradePlanSvg />}
          </span>
        </UpgradePlanTooltip>
      ),
      disabled:
        !workspace.permissions.inviteContributor ||
        !workspace.permissions.inviteMember ||
        !workspace.permissions.inviteTeamLead,
      onClick: () => {
        if (isUsersCountLimitReached) {
          return;
        }
        dispatch(
          toggleWorkspaceInvitationModal({
            visible: true,
            workspace
          })
        );
      }
    },
    {
      key: 'manage_workspace',
      label: 'Manage workspace',
      disabled: !workspace.permissions.changeDetails,
      onClick: () => {
        dispatch(
          toggleWorkspaceCreationModal({
            visible: true,
            isEditing: true,
            workspace
          })
        );
      }
    },
    {
      key: 'members',
      label: 'Members & Groups',
      disabled: !workspace.permissions.listMembers,
      onClick: () => {
        dispatch(
          toggleWorkspaceMembersModal({
            visible: true,
            workspace
          })
        );
      }
    },
    {
      key: 'leave workspace',
      label: 'Leave workspace',
      disabled: currentOrganization.entity?.permissions.listWorkspaces,
      onClick: () => {
        dispatch(
          toggleWorkspaceOrganizationLeaveModal({
            visible: true,
            entity: workspace,
            entityName: 'workspace'
          })
        );
      }
    },
    {
      key: 'archive_workspace',
      label: 'Archive workspace',
      disabled: !workspace?.permissions.delete,
      onClick: () => {
        dispatch(
          toggleArchiveWorkspaceModal({
            visible: true,
            workspace
          })
        );
      }
    },
    {
      key: 'delete_workspace',
      label: 'Delete workspace',
      disabled: !workspace?.permissions.delete,
      onClick: () => {
        dispatch(
          toggleWorkspaceOrganizationDeletionModal({
            entityName: 'workspace',
            visible: true,
            entity: workspace
          })
        );
      }
    }
  ];
  return (
    <Menu
      className="entities-dropdown-menu-overlay"
      items={items}
      onClick={() => {
        if (setIsDropDownOpen) setIsDropDownOpen(false);
      }}
    />
  );
}

function WorkspaceDropDownMenu({ workspace }: { workspace: WorkspaceItemDto }) {
  const [isDropDownOpen, setIsDropDownOpen] = useState<boolean>(false);

  return (
    <Dropdown
      trigger={['click']}
      onOpenChange={(value) => setIsDropDownOpen(value)}
      open={isDropDownOpen}
      overlay={
        <WorkspaceMenu
          setIsDropDownOpen={setIsDropDownOpen}
          workspace={workspace}
        />
      }
      overlayClassName="entities-dropdown-menu-overlay"
    >
      <DropdownMenuButton onOpenChange={setIsDropDownOpen} />
    </Dropdown>
  );
}

export default WorkspaceDropDownMenu;
